import { useState, useCallback, useRef } from "react";
import type { Location } from "../data/locations.js";

const MAP_IMAGE = "https://www.senso-ji.jp/images_en/visit_ill01.jpg";

interface PinData {
  id: string;
  number: number;
  name: string;
  nameJa: string;
  description: string;
  x: number;
  y: number;
  mapsUrl: string;
  tip: string;
  /** Photo URLs, one per line */
  photoUrls: string;
}

const EMPTY_PIN: PinData = {
  id: "",
  number: 1,
  name: "",
  nameJa: "",
  description: "",
  x: 50,
  y: 50,
  mapsUrl: "",
  tip: "",
  photoUrls: "",
};

export function PinEditor() {
  const [pins, setPins] = useState<PinData[]>([]);
  const [selectedPin, setSelectedPin] = useState<number | null>(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"" | "saving" | "saved" | "error">("");
  const [, setLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Load pins from server on mount
  useState(() => {
    fetch("/api/pins")
      .then((r) => r.json())
      .then((data: PinData[]) => {
        if (Array.isArray(data) && data.length > 0) {
          setPins(data);
        } else {
          // Fallback to localStorage
          const saved = localStorage.getItem("kra-pin-editor");
          if (saved) setPins(JSON.parse(saved));
        }
        setLoaded(true);
      })
      .catch(() => {
        const saved = localStorage.getItem("kra-pin-editor");
        if (saved) setPins(JSON.parse(saved));
        setLoaded(true);
      });
  });

  const save = useCallback((updated: PinData[]) => {
    setPins(updated);
    localStorage.setItem("kra-pin-editor", JSON.stringify(updated));
  }, []);

  /** Publish pins to server (Cloudflare KV) */
  const publishToServer = useCallback(async () => {
    setSaveStatus("saving");
    try {
      const res = await fetch("/api/pins?admin=1", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pins),
      });
      if (res.ok) {
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    }
  }, [pins]);

  const handleMapClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPlacing || !imageRef.current) return;
      const rect = imageRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      const newPin: PinData = {
        ...EMPTY_PIN,
        id: `pin-${Date.now()}`,
        number: pins.length + 1,
        x: Math.round(x * 10) / 10,
        y: Math.round(y * 10) / 10,
      };
      const updated = [...pins, newPin];
      save(updated);
      setSelectedPin(updated.length - 1);
      setIsPlacing(false);
    },
    [isPlacing, pins, save]
  );

  const updatePin = useCallback(
    (index: number, field: keyof PinData, value: string | number) => {
      const updated = [...pins];
      updated[index] = { ...updated[index], [field]: value };
      save(updated);
    },
    [pins, save]
  );

  const deletePin = useCallback(
    (index: number) => {
      const updated = pins.filter((_, i) => i !== index);
      // Re-number
      updated.forEach((p, i) => (p.number = i + 1));
      save(updated);
      setSelectedPin(null);
    },
    [pins, save]
  );

  const handlePinDrag = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!imageRef.current) return;
      const img = imageRef.current;

      const onMove = (ev: MouseEvent) => {
        const rect = img.getBoundingClientRect();
        const x = ((ev.clientX - rect.left) / rect.width) * 100;
        const y = ((ev.clientY - rect.top) / rect.height) * 100;
        const updated = [...pins];
        updated[index] = {
          ...updated[index],
          x: Math.round(Math.max(0, Math.min(100, x)) * 10) / 10,
          y: Math.round(Math.max(0, Math.min(100, y)) * 10) / 10,
        };
        setPins(updated);
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        localStorage.setItem("kra-pin-editor", JSON.stringify(pins));
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [pins]
  );

  const exportJson = useCallback(() => {
    const output: Partial<Location>[] = pins.map((p) => {
      const photos = p.photoUrls
        .split("\n")
        .map((url) => url.trim())
        .filter(Boolean)
        .map((url) => ({ url, alt: p.name || `Spot ${p.number}` }));
      return {
        id: p.id,
        number: p.number,
        name: p.name || `Spot ${p.number}`,
        nameJa: p.nameJa || "",
        description: p.description || "",
        x: p.x,
        y: p.y,
        mapsUrl: p.mapsUrl || "",
        photos,
        tip: p.tip || undefined,
      };
    });
    return JSON.stringify(output, null, 2);
  }, [pins]);

  return (
    <div className="fixed inset-0 flex bg-surface-0">
      {/* Map area */}
      <div
        className="flex-1 relative overflow-auto bg-surface-2"
        onClick={handleMapClick}
        style={{ cursor: isPlacing ? "crosshair" : "default" }}
      >
        <div className="relative inline-block">
          <img
            ref={imageRef}
            src={MAP_IMAGE}
            alt="Map"
            className="block max-h-screen"
            draggable={false}
          />

          {/* Pins */}
          {pins.map((pin, i) => (
            <div
              key={pin.id}
              className={`absolute flex flex-col items-center cursor-grab active:cursor-grabbing`}
              style={{
                left: `${pin.x}%`,
                top: `${pin.y}%`,
                transform: "translate(-50%, -100%)",
                zIndex: selectedPin === i ? 50 : 10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPin(i);
              }}
              onMouseDown={(e) => handlePinDrag(i, e)}
            >
              <div
                className={`flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                  selectedPin === i
                    ? "w-10 h-10 bg-kra text-white shadow-lg ring-2 ring-white"
                    : "w-8 h-8 bg-white text-kra shadow-md ring-2 ring-kra"
                }`}
              >
                {pin.number}
              </div>
              <div className="w-0.5 h-1.5 bg-kra" />
              <div className="text-[9px] mt-0.5 bg-white/90 px-1 rounded text-ink-1 whitespace-nowrap">
                {pin.name || `Spot ${pin.number}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-80 bg-white border-l border-surface-2 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="p-3 border-b border-surface-2 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={() => setIsPlacing(!isPlacing)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                isPlacing
                  ? "bg-kra text-white"
                  : "bg-surface-1 text-ink-1 hover:bg-surface-2"
              }`}
            >
              {isPlacing ? "Click map to place..." : "+ Add Pin"}
            </button>
            <button
              onClick={() => setShowExport(!showExport)}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-surface-1 text-ink-1 hover:bg-surface-2"
            >
              JSON
            </button>
          </div>
          <button
            onClick={publishToServer}
            disabled={saveStatus === "saving"}
            className={`w-full py-2.5 rounded-lg text-xs font-semibold transition-all ${
              saveStatus === "saved"
                ? "bg-green-600 text-white"
                : saveStatus === "error"
                ? "bg-red-500 text-white"
                : "bg-kra text-white hover:bg-kra-dark active:scale-[0.98]"
            }`}
          >
            {saveStatus === "saving"
              ? "Saving..."
              : saveStatus === "saved"
              ? "Saved!"
              : saveStatus === "error"
              ? "Error — try again"
              : "💾 Save & Publish"}
          </button>
        </div>

        {/* Pin list or edit form */}
        <div className="flex-1 overflow-y-auto">
          {showExport ? (
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-ink-1">JSON Export</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(exportJson());
                  }}
                  className="text-xs text-kra font-medium"
                >
                  Copy
                </button>
              </div>
              <textarea
                value={exportJson()}
                readOnly
                className="w-full h-64 text-[10px] font-mono bg-surface-1 rounded-lg p-2 text-ink-1 resize-none"
              />
            </div>
          ) : selectedPin !== null && pins[selectedPin] ? (
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-ink-0">
                  Pin #{pins[selectedPin].number}
                </span>
                <div className="flex gap-2">
                  <span className="text-[10px] text-ink-2">
                    x:{pins[selectedPin].x}% y:{pins[selectedPin].y}%
                  </span>
                  <button
                    onClick={() => deletePin(selectedPin)}
                    className="text-xs text-red-500"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {(
                [
                  ["name", "Name (EN)", "text"],
                  ["nameJa", "Name (JP)", "text"],
                  ["description", "Description", "textarea"],
                  ["photoUrls", "Photo URLs (one per line)", "textarea"],
                  ["mapsUrl", "Google Maps URL", "text"],
                  ["tip", "Photo tip", "text"],
                ] as const
              ).map(([field, label, type]) => (
                <div key={field}>
                  <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                    {label}
                  </label>
                  {type === "textarea" ? (
                    <textarea
                      value={pins[selectedPin][field] as string}
                      onChange={(e) =>
                        updatePin(selectedPin, field, e.target.value)
                      }
                      className="w-full mt-1 px-2 py-1.5 text-xs bg-surface-1 rounded-lg text-ink-0 resize-none"
                      rows={3}
                    />
                  ) : (
                    <input
                      value={pins[selectedPin][field] as string}
                      onChange={(e) =>
                        updatePin(selectedPin, field, e.target.value)
                      }
                      className="w-full mt-1 px-2 py-1.5 text-xs bg-surface-1 rounded-lg text-ink-0"
                    />
                  )}
                </div>
              ))}

              {/* Photo previews */}
              {pins[selectedPin].photoUrls && (
                <div>
                  <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                    Photo Preview
                  </label>
                  <div className="flex gap-1.5 mt-1 overflow-x-auto">
                    {pins[selectedPin].photoUrls
                      .split("\n")
                      .map((u) => u.trim())
                      .filter(Boolean)
                      .map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt=""
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 space-y-1">
              <p className="text-xs text-ink-2 mb-2">
                {pins.length} pins — click a pin to edit, drag to move
              </p>
              {pins.map((pin, i) => (
                <button
                  key={pin.id}
                  onClick={() => setSelectedPin(i)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-1 text-left transition-colors"
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-kra text-white text-[10px] font-bold">
                    {pin.number}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-ink-0 truncate">
                      {pin.name || `Spot ${pin.number}`}
                    </div>
                    <div className="text-[10px] text-ink-2">
                      x:{pin.x}% y:{pin.y}%
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
